import { CogIcon, DownloadIcon, KeyIcon } from '@heroicons/react/solid'
import {
  useDownloadRestAdapterConfig,
  useGantnerDevices,
  useOpenGantnerDevice,
} from 'api/GantnerDevices'
import { Dropdown, DropdownDivider, DropdownItem } from 'ui/components/Dropdown'
import { PaginatedMiniList } from 'ui/components/PaginatedMiniList'
import { ResourceListItem } from 'ui/components/ResourceListItem'
import { SettingInputBlock } from 'ui/components/Settings/SettingInputBlock'
import { SettingsPage } from 'ui/components/Settings/SettingsPage'
import { DateTime } from 'luxon'
import { EnabledIndicator } from 'components/EnabledIndicator'
import { useState } from 'react'
import { DeletePrompt } from './DeletePrompt'
import { NotificationSettings } from './NotificationSettings'
import { useCurrentUserCanForCurrentSite } from 'hooks/UseCurrentUserCan'
import { SETTINGS_MANAGE } from 'constants/permissions'
import { ReadOnlyBadge } from 'ui/components/Form/NamedBadges/NamedBadges'
import { LaunchGuideButton } from 'components/LaunchGuideButton'
import { useCurrentSiteId } from 'contexts/SiteConfig'
import { Notification, useNotificationContext } from 'ui/components/Notification'
import { Device1 as Device } from '@trybeapp/sdk'
import { Trans, useTranslation } from 'react-i18next'
import { DeleteDropdownItem, EditDropdownItemLink } from 'ui/components/NamedDropdownItems'
import { Toggle } from 'ui/components/Form/Toggle'
import { useGetGantnerConfig, useUpdateGantnerConfig } from 'api/Integrations/Gantner'

export const canRemotelyOpenDevice = (device: Device) => {
  // The device can be remotely opened if it's one of
  // the models that supports remotely opening
  const allowedDevices = ['GT7', 'GRT7']

  return device.last_connected_at !== null && allowedDevices.includes(device.type)
}

export const Gantner = () => {
  const { t } = useTranslation()
  const can = useCurrentUserCanForCurrentSite()
  const userCanManageSettings = can(SETTINGS_MANAGE)
  const [deleting, setDeleting] = useState<Device>()
  const siteId = useCurrentSiteId()
  const notifications = useNotificationContext()

  const {
    isLoading,
    error,
    data: { data: gantnerDevices, meta, has_g6xxx_devices } = {},
  } = useGantnerDevices()

  const { mutate: openGate } = useOpenGantnerDevice()

  const { data: { data: { order_qr_code_lookup_enabled: orderQrCodeLookupEnabled } = {} } = {} } =
    useGetGantnerConfig()

  const { mutate: updateQrCodeSetting } = useUpdateGantnerConfig()

  const { mutateAsync: downloadRestAdapterConfig, isLoading: isDownloadingRestAdapterConfig } =
    useDownloadRestAdapterConfig()

  const handleDownloadRestAdapterConfig = async () => {
    const file = await downloadRestAdapterConfig()
    const blob = new Blob([file], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'RestAdapterConfiguration.xml'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const handleChange = (checked: boolean) => {
    const updateValue = {
      order_qr_code_lookup_enabled: checked,
    }

    updateQrCodeSetting(
      { siteId, updateSiteGantnerConfig: updateValue },
      {
        onSuccess: () => {
          notifications.notify(
            <Notification
              title={
                checked
                  ? t(
                      'settings.integrations.gantner.additional_settings.order_qr_toggle.enabled_notification.title'
                    )
                  : t(
                      'settings.integrations.gantner.additional_settings.order_qr_toggle.disabled_notification.title'
                    )
              }
              description={
                checked
                  ? t(
                      'settings.integrations.gantner.additional_settings.order_qr_toggle.enabled_notification.description'
                    )
                  : t(
                      'settings.integrations.gantner.additional_settings.order_qr_toggle.disabled_notification.description'
                    )
              }
              variant="success"
              autoDismiss
            />
          )
        },
        onError: (response: Response) => {
          notifications.notifyApiError(response)
        },
      }
    )
  }

  return (
    <SettingsPage
      title={t('settings.integrations.gantner.title')}
      previousPages={[
        { title: t('settings_page.previous_page.integrations'), path: '/settings/integrations' },
      ]}
      rightOfTitle={
        <div className="flex items-baseline space-x-2">
          <LaunchGuideButton id="launch-gantner-guide" intercomArticleId="253942" />
          {!userCanManageSettings && <ReadOnlyBadge />}
        </div>
      }
    >
      <SettingInputBlock
        label={t('settings.integrations.gantner.devices.title')}
        description={[
          t('settings.integrations.gantner.devices.description_line_first'),
          t('settings.integrations.gantner.devices.description_line_second'),
          <span className="text-sm text-gray-500 ">
            <Trans
              i18nKey="settings.integrations.gantner.devices.description_line_third"
              components={[
                <span className="chat-with-us font-medium text-violet cursor-pointer hover:underline" />,
              ]}
            />
          </span>,
          t('settings.integrations.gantner.devices.description_line_fourth'),
        ]}
      >
        <PaginatedMiniList
          emptyMessage={t('settings.integrations.gantner.devices.empty_message')}
          isLoading={isLoading}
          error={error?.response}
          meta={meta}
          rows={gantnerDevices?.map((gantnerDevice) => (
            <ResourceListItem
              key={gantnerDevice.id}
              title={gantnerDevice.name}
              subtitle={
                <div className="pl-2 flex flex-row gap-x-2">
                  <EnabledIndicator
                    secondary
                    value={gantnerDevice.last_connected_at !== null}
                    label={
                      gantnerDevice.last_connected_at
                        ? t('settings.integrations.gantner.devices.device.last_used', {
                            date: DateTime.fromJSDate(gantnerDevice.last_connected_at).toRelative(),
                          })
                        : t('settings.integrations.gantner.devices.device.not_used_recently')
                    }
                  />
                </div>
              }
              dropdownItems={[
                <>
                  <DropdownItem
                    label={t('settings.integrations.gantner.devices.device.dropdown.open')}
                    icon={KeyIcon}
                    disabled={!canRemotelyOpenDevice(gantnerDevice)}
                    onClick={() => {
                      openGate([siteId, gantnerDevice.id], {
                        onSuccess: () => {
                          notifications.notify(
                            <Notification
                              title={t(
                                'settings.integrations.gantner.devices.device.notification.title'
                              )}
                              description=""
                              variant="success"
                              autoDismiss
                            />
                          )
                        },
                        onError: (e) => {
                          notifications.notifyApiError(e)
                        },
                      })
                    }}
                  />
                  <DropdownDivider />
                  <EditDropdownItemLink
                    key="edit"
                    href={`/settings/integrations/gantner/devices/${gantnerDevice.id}`}
                    permission={userCanManageSettings}
                  />
                  <DropdownDivider />
                  <DeleteDropdownItem
                    key="delete"
                    onClick={() => setDeleting(gantnerDevice)}
                    permission={userCanManageSettings}
                  />
                </>,
              ]}
            />
          ))}
        />

        {has_g6xxx_devices && (
          <div className="flex justify-end mt-4">
            <Dropdown buttonLeadingIcon={CogIcon} buttonHideLabel>
              <DropdownItem
                icon={DownloadIcon}
                label="Download G6xxx config"
                hideOnClick={false}
                loading={isDownloadingRestAdapterConfig}
                onClick={handleDownloadRestAdapterConfig}
              />
            </Dropdown>
          </div>
        )}

        <DeletePrompt onClose={() => setDeleting(undefined)} deleting={deleting} />
      </SettingInputBlock>

      <NotificationSettings />

      <SettingInputBlock label={t('settings.integrations.gantner.additional_settings.title')}>
        <Toggle
          label={t('settings.integrations.gantner.additional_settings.order_qr_toggle.label')}
          description={[
            t(
              'settings.integrations.gantner.additional_settings.order_qr_toggle.description_line_first'
            ),
            t(
              'settings.integrations.gantner.additional_settings.order_qr_toggle.description_line_second'
            ),
          ]}
          value={orderQrCodeLookupEnabled}
          onChange={handleChange}
          disabled={!userCanManageSettings}
        />
      </SettingInputBlock>
    </SettingsPage>
  )
}
